import React, { useState } from "react";
import {
  EuiButton,
  EuiButtonEmpty,
  EuiButtonGroup,
  EuiCallOut,
  EuiConfirmModal,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiForm,
  EuiFormRow,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiSelect,
  EuiSpacer,
  EuiText,
  EuiTextArea,
} from "@elastic/eui";
import { OplItem, OplItemType, OplMeeting } from "src/questionnaires";
import { useAnketaI18n } from "src/internationalization";
import { fmtDate, isoDay, typeLabel } from "./format";
import { OplAttendeeInput, OplItemInput } from "./api";
import { ParticipantEditor } from "./participant-editor";

const i18n = "anketa.opsanio.opl";

const useT = () => {
  const { translate } = useAnketaI18n();
  return (k: string, d: string, values?: Record<string, unknown>) =>
    translate(`${i18n}.${k}`, { defaultMessage: d, values });
};

/** Next week, the default a new commitment gets unless someone says otherwise. */
const weekOut = (from: string | null): string => {
  const d = from ? new Date(from) : new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
};

// ---------------------------------------------------------------------------
// New point
// ---------------------------------------------------------------------------

export interface NewPointDialogProps {
  round: OplMeeting | null;
  /** Owner candidates across all rounds - work may go to someone not in the room. */
  people: Array<{ value: string; label: string; inRound: boolean }>;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (value: OplItemInput) => void;
}

/**
 * Raising a point. The type drives the form: an Information carries neither a
 * due date nor an owner, so both are disabled rather than merely optional -
 * the concept's table made visible instead of validated after the fact.
 */
export const NewPointDialog = ({
  round,
  people,
  saving,
  error,
  onCancel,
  onSubmit,
}: NewPointDialogProps): JSX.Element => {
  const t = useT();
  const [type, setType] = useState<OplItemType>("A");
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState(weekOut(round?.date ?? null));
  const [owner, setOwner] = useState("");

  const isInfo = type === "I";
  const valid =
    title.trim() !== "" && (isInfo || (dueDate !== "" && owner !== ""));

  return (
    <EuiModal onClose={onCancel} style={{ minWidth: 520 }}>
      <EuiModalHeader>
        <EuiModalHeaderTitle>
          <h2>{t("new.title", "Neuer Punkt")}</h2>
        </EuiModalHeaderTitle>
      </EuiModalHeader>
      <EuiModalBody>
        <EuiForm component="form">
          <EuiFormRow label={t("detail.entry", "Eingang")}>
            <EuiText size="s">
              <span className="oplMono">{round?.key ?? "-"}</span>
            </EuiText>
          </EuiFormRow>

          <EuiFormRow label={t("filter.type", "Typ")}>
            <EuiButtonGroup
              legend={t("filter.type", "Typ")}
              buttonSize="compressed"
              options={(["I", "A", "E"] as OplItemType[]).map((v) => ({
                id: v,
                label: `${v} · ${typeLabel(t, v)}`,
              }))}
              idSelected={type}
              onChange={(id: string) => setType(id as OplItemType)}
            />
          </EuiFormRow>

          <EuiFormRow label={t("new.titleField", "Titel")} fullWidth>
            <EuiFieldText
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("new.titlePlaceholder", "Kleiner Zuschnitt, eine Sache")}
              autoFocus
            />
          </EuiFormRow>

          <EuiFormRow
            label={t("col.due", "Fällig")}
            helpText={isInfo ? t("type.hintI", "Typ I trägt kein Datum.") : undefined}
          >
            <input
              type="date"
              value={isInfo ? "" : dueDate}
              disabled={isInfo}
              onChange={(e) => setDueDate(e.target.value)}
              aria-label={t("col.due", "Fällig")}
              style={{
                padding: "6px 8px",
                border: "1px solid var(--opl-line)",
                borderRadius: 4,
                background: "var(--opl-card)",
                fontSize: 12.5,
              }}
            />
          </EuiFormRow>

          <EuiFormRow label={t("col.owner", "Verantwortlich")}>
            <EuiSelect
              value={isInfo ? "" : owner}
              disabled={isInfo}
              options={[
                { value: "", text: "-" },
                ...people.map((p) => ({
                  value: p.value,
                  text: `${p.label}${
                    p.inRound ? "" : `  · ${t("notInRound", "nicht in dieser Runde")}`
                  }`,
                })),
              ]}
              onChange={(e) => setOwner(e.target.value)}
              aria-label={t("col.owner", "Verantwortlich")}
            />
          </EuiFormRow>

          {error && (
            <>
              <EuiSpacer size="s" />
              <EuiText size="xs" color="danger">
                {error}
              </EuiText>
            </>
          )}
        </EuiForm>
      </EuiModalBody>
      <EuiModalFooter>
        <EuiButtonEmpty onClick={onCancel}>{t("cancel", "Abbrechen")}</EuiButtonEmpty>
        <EuiButton
          fill
          isLoading={saving}
          disabled={!valid || saving}
          onClick={() =>
            onSubmit({
              title: title.trim(),
              type,
              ...(isInfo ? {} : { dueDate, owner }),
            })
          }
        >
          {t("new.submit", "Aufnehmen")}
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  );
};

// ---------------------------------------------------------------------------
// Split
// ---------------------------------------------------------------------------

export interface SplitDialogProps {
  item: OplItem;
  round: OplMeeting | null;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (titles: string[]) => void;
}

export const SplitDialog = ({
  item,
  round,
  saving,
  onCancel,
  onSubmit,
}: SplitDialogProps): JSX.Element => {
  const t = useT();
  const [titles, setTitles] = useState<string[]>(["", ""]);

  const filled = titles.map((x) => x.trim()).filter(Boolean);
  const valid = filled.length >= 2;

  return (
    <EuiModal onClose={onCancel} style={{ minWidth: 560 }}>
      <EuiModalHeader>
        <EuiModalHeaderTitle>
          <h2>{t("split.title", "Punkt aufteilen")}</h2>
        </EuiModalHeaderTitle>
      </EuiModalHeader>
      <EuiModalBody>
        <EuiText size="s" color="subdued">
          <p>
            {item.key} → {round?.key ?? "-"}. {" "}
            {t(
              "split.inherit",
              "Kinder erben die Fälligkeit des Elternteils, nicht das Datum des Splits.",
            )}{" "}
            <strong>{fmtDate(item.dueDate)}</strong>
          </p>
        </EuiText>
        <EuiSpacer size="m" />

        {titles.map((value, index) => (
          <EuiFormRow key={index} label={`${index + 1}.`} fullWidth>
            <EuiFieldText
              fullWidth
              value={value}
              placeholder={t("split.childPlaceholder", "Titel des Nachfolgepunktes")}
              onChange={(e) => {
                const next = [...titles];
                next[index] = e.target.value;
                setTitles(next);
              }}
            />
          </EuiFormRow>
        ))}

        <EuiSpacer size="s" />
        <EuiButtonEmpty
          size="xs"
          iconType="plusInCircle"
          onClick={() => setTitles([...titles, ""])}
        >
          {t("split.addChild", "weiteres Kind")}
        </EuiButtonEmpty>

        {!valid && (
          <>
            <EuiSpacer size="s" />
            <EuiText size="xs" color="subdued">
              {t("split.needTwo", "Ein Split braucht mindestens zwei Nachfolgepunkte.")}
            </EuiText>
          </>
        )}
      </EuiModalBody>
      <EuiModalFooter>
        <EuiButtonEmpty onClick={onCancel}>{t("cancel", "Abbrechen")}</EuiButtonEmpty>
        <EuiButton
          fill
          isLoading={saving}
          disabled={!valid || saving}
          onClick={() => onSubmit(filled)}
        >
          {t("split.submit", "Aufteilen")}
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  );
};

// ---------------------------------------------------------------------------
// Absence warning
// ---------------------------------------------------------------------------

export interface AbsenceWarningProps {
  message: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * The deliberate step in front of assigning to someone who is not in the room.
 * Not a block - the round may have good reason - but never a silent default.
 */
export const AbsenceWarning = ({
  message,
  busy,
  onCancel,
  onConfirm,
}: AbsenceWarningProps): JSX.Element => {
  const t = useT();
  return (
    <EuiConfirmModal
      title={t("absence.title", "Zuweisung an abwesende Person")}
      onCancel={onCancel}
      onConfirm={onConfirm}
      cancelButtonText={t("cancel", "Abbrechen")}
      confirmButtonText={t("absence.confirm", "Trotzdem zuweisen")}
      buttonColor="warning"
      isLoading={busy}
    >
      <EuiText size="s">
        <p>{message}</p>
      </EuiText>
      <EuiSpacer size="s" />
      <EuiCallOut
        size="s"
        color="warning"
        iconType="iInCircle"
        title={t(
          "absence.note",
          "Der Punkt wird im Protokoll als „in Abwesenheit zugewiesen“ markiert.",
        )}
      />
    </EuiConfirmModal>
  );
};

// ---------------------------------------------------------------------------
// Meeting head editor
// ---------------------------------------------------------------------------

export interface MeetingDialogProps {
  meeting: OplMeeting | null;
  saving: boolean;
  /** Known appliance contacts, offered as quick adds. */
  contactSuggestions?: Array<{ value: string; label: string }>;
  /**
   * Seeds the participant list of a brand-new round from the appliance's
   * questionnaire contacts. Ignored when editing an existing round - that list
   * has been curated and must not be overwritten from the questionnaire.
   */
  defaultParticipants?: OplAttendeeInput[];
  onCancel: () => void;
  onSubmit: (value: {
    date: string;
    title?: string;
    durationMinutes?: number;
    recorder?: string;
    moderator?: string;
    notes?: string;
    attendees: OplAttendeeInput[];
  }) => void;
}

export const MeetingDialog = ({
  meeting,
  saving,
  contactSuggestions = [],
  defaultParticipants = [],
  onCancel,
  onSubmit,
}: MeetingDialogProps): JSX.Element => {
  const t = useT();
  const [date, setDate] = useState(isoDay(meeting?.date) || isoDay(new Date().toISOString()));
  const [time, setTime] = useState(
    meeting ? new Date(meeting.date).toTimeString().slice(0, 5) : "10:00",
  );
  const [title, setTitle] = useState(meeting?.title ?? "");
  const [duration, setDuration] = useState(String(meeting?.durationMinutes ?? 50));
  const [recorder, setRecorder] = useState(meeting?.recorder ?? "");
  const [moderator, setModerator] = useState(meeting?.moderator ?? "");
  const [notes, setNotes] = useState(meeting?.notes ?? "");
  const [attendees, setAttendees] = useState<OplAttendeeInput[]>(
    meeting
      ? (meeting.attendees ?? []).map((a) => ({
          name: a.name,
          email: a.email ?? "",
          source: a.source,
          present: a.present,
          isGuest: a.isGuest,
          organisation: a.organisation ?? "",
          fromTime: a.fromTime ?? "",
          untilTime: a.untilTime ?? "",
          absenceReason: a.absenceReason ?? undefined,
          substitute: a.substitute ?? "",
        }))
      : defaultParticipants,
  );

  // A participant without a name cannot be saved - the name is the identity.
  const valid = date !== "" && attendees.every((a) => a.name.trim() !== "");

  return (
    <EuiModal onClose={onCancel} style={{ minWidth: 520 }}>
      <EuiModalHeader>
        <EuiModalHeaderTitle>
          <h2>{meeting ? t("head.edit", "Kopf bearbeiten") : t("empty.create", "Erste Runde anlegen")}</h2>
        </EuiModalHeaderTitle>
      </EuiModalHeader>
      <EuiModalBody>
        <EuiForm component="form">
          <EuiFlexGroup gutterSize="s" responsive={false}>
            <EuiFlexItem>
              <EuiFormRow label={t("head.date", "Datum")}>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  aria-label={t("head.date", "Datum")}
                  style={{
                    padding: "6px 8px",
                    border: "1px solid var(--opl-line)",
                    borderRadius: 4,
                    background: "var(--opl-card)",
                    fontSize: 12.5,
                  }}
                />
              </EuiFormRow>
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiFormRow label={t("head.start", "Start")}>
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  aria-label={t("head.start", "Start")}
                  style={{
                    padding: "6px 8px",
                    border: "1px solid var(--opl-line)",
                    borderRadius: 4,
                    background: "var(--opl-card)",
                    fontSize: 12.5,
                  }}
                />
              </EuiFormRow>
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiFormRow label={t("head.duration", "Dauer")}>
                <EuiFieldText
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  append="min"
                />
              </EuiFormRow>
            </EuiFlexItem>
          </EuiFlexGroup>

          <EuiFormRow label={t("new.titleField", "Titel")} fullWidth>
            <EuiFieldText
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="KW 33"
            />
          </EuiFormRow>

          <EuiFormRow label={t("head.recorder", "Protokoll")} fullWidth>
            <EuiFieldText
              fullWidth
              value={recorder}
              onChange={(e) => setRecorder(e.target.value)}
            />
          </EuiFormRow>

          <EuiFormRow
            label={t("head.moderator", "Moderator")}
            helpText={t("head.moderatorDefault", "Vorbelegung: Protokollführer")}
            fullWidth
          >
            <EuiFieldText
              fullWidth
              value={moderator}
              onChange={(e) => setModerator(e.target.value)}
              placeholder={recorder}
            />
          </EuiFormRow>

          {/* The minutes live here rather than in the 296px sidebar: this is
              what the summary mail carries, and it needs room to write in. */}
          <EuiFormRow
            label={t("notes", "Protokoll")}
            helpText={t("notes.help", "Markdown. Wird mit der Zusammenfassung versendet.")}
            fullWidth
          >
            <EuiTextArea
              fullWidth
              compressed
              rows={5}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              aria-label={t("notes", "Protokoll")}
            />
          </EuiFormRow>

          <EuiSpacer size="m" />
          <div className="oplEyebrow">{t("participants", "Teilnehmer")}</div>
          <EuiSpacer size="s" />
          <ParticipantEditor
            value={attendees}
            onChange={setAttendees}
            suggestions={contactSuggestions}
            // Only while creating: an existing round's list is authoritative and
            // nothing is re-seeded into it.
            lockedKeys={
              meeting
                ? []
                : defaultParticipants.map((p) => (p.email ?? "").trim() || p.name)
            }
          />
        </EuiForm>
      </EuiModalBody>
      <EuiModalFooter>
        <EuiButtonEmpty onClick={onCancel}>{t("cancel", "Abbrechen")}</EuiButtonEmpty>
        <EuiButton
          fill
          isLoading={saving}
          disabled={!valid || saving}
          onClick={() =>
            onSubmit({
              date: new Date(`${date}T${time || "10:00"}:00`).toISOString(),
              title: title.trim(),
              durationMinutes: Number.parseInt(duration, 10) || undefined,
              recorder: recorder.trim(),
              moderator: moderator.trim() || recorder.trim(),
              notes,
              attendees,
            })
          }
        >
          {t("save", "Speichern")}
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  );
};
